import { useEffect, useRef, useCallback } from 'react';

const TIMEOUT_MS = 12 * 60 * 60 * 1000; // 12 hours

// Activity events that reset the timer
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];

/**
 * Fires `onLogout` after TIMEOUT_MS of user inactivity.
 * Only active when `enabled` is true (i.e. a user is logged in).
 * Any mouse, keyboard, touch, or scroll event resets the timer.
 */
export default function useInactivityLogout(onLogout, enabled) {
  const timerRef = useRef(null);
  const onLogoutRef = useRef(onLogout);

  // Keep the ref current without restarting the effect
  useEffect(() => {
    onLogoutRef.current = onLogout;
  }, [onLogout]);

  const reset = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      onLogoutRef.current('inactivity');
    }, TIMEOUT_MS);
  }, []);

  useEffect(() => {
    if (!enabled) {
      // User logged out — clear any pending timer
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }

    // Start the timer and attach activity listeners
    reset();
    ACTIVITY_EVENTS.forEach(evt => window.addEventListener(evt, reset, { passive: true }));

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      ACTIVITY_EVENTS.forEach(evt => window.removeEventListener(evt, reset));
    };
  }, [enabled, reset]);
}
